import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationOnlyPageService } from '../services/locationOnlyPageService';
import { message } from 'antd';
import { LocationOnlyPage, CreateLocationOnlyPageRequest } from '../types/locationOnlyPage';

export const useLocationOnlyPages = (tenantId: string, status?: string) => {
  return useQuery({
    queryKey: ['location-only-pages', tenantId, status],
    queryFn: () => locationOnlyPageService.list(tenantId, status),
    enabled: !!tenantId,
  });
};

export const useLocationOnlyPage = (id: string) => {
  return useQuery({
    queryKey: ['location-only-page', id],
    queryFn: () => locationOnlyPageService.get(id),
    enabled: !!id,
  });
};

export const useCreateLocationOnlyPage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLocationOnlyPageRequest) => locationOnlyPageService.create(data),
    onSuccess: (_, variables) => {
      message.success('Location-only page creation started');
      queryClient.invalidateQueries({ queryKey: ['location-only-pages', variables.tenant_id] });
    },
    onError: () => {
      message.error('Failed to create location-only page');
    }
  });
};

export const useGenerateLocationOnlyPageWithCrew = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: locationOnlyPageService.generateWithCrew,
        onSuccess: () => {
            message.success('Crew kickoff successful');
            // Invalidate might not be enough if it's async, but good practice
            queryClient.invalidateQueries({ queryKey: ['location-only-pages'] }); 
        },
        onError: () => {
            message.error('Failed to start crew');
        }
    })
}

export const useUpdateLocationOnlyPage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({id, data}: {id: string, data: Partial<LocationOnlyPage>}) => locationOnlyPageService.update(id, data),
        onSuccess: (_, vars) => {
             message.success('Page updated');
             queryClient.invalidateQueries({ queryKey: ['location-only-pages'] });
             queryClient.invalidateQueries({ queryKey: ['location-only-page', vars.id] });
        }
    })
}

export const useDeleteLocationOnlyPage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => locationOnlyPageService.delete(id),
        onSuccess: () => {
             message.success('Page deleted');
             queryClient.invalidateQueries({ queryKey: ['location-only-pages'] });
        }
    })
}

export const usePublishLocationOnlyPage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => locationOnlyPageService.publish(id),
        onSuccess: (_, id) => {
             message.success('Page published');
             queryClient.invalidateQueries({ queryKey: ['location-only-pages'] });
             queryClient.invalidateQueries({ queryKey: ['location-only-page', id] });
        }
    })
}
